/**
 * printer-adapters-z — Adapters d'impression rapport Z/X VisionPOS.
 *
 * Extrait de printer-adapters.ts (Phase 3c — split modules > 500 LOC).
 * Copie stricte (4.A.16) — les chaînes NF525 sont byte-identiques à printer-adapters.ts.
 *
 * Contient :
 *   - sendViaAidlZReport()        — rapport Z via AIDL natif Sunmi
 *   - sendViaCloudPrinterZReport()— rapport Z via Sunmi PrinterX (D3 Pro)
 *
 * INVARIANT 4.A.16 : les textes NF525 (CLOTURE Z, RAPPORT X, nf525Mention,
 * legalName) sont rendus byte-identiques à buildEscPosZReport — toute
 * modification ici doit être portée en parallèle dans escpos-formatter.ts.
 */

import { EDITOR } from '@/config/editor';
import type { ZReportData } from './escpos-formatter';
import { zEncoursLines } from './z-encours-lines';

// ── sendViaAidlZReport ────────────────────────────────────────────────────────

/**
 * Imprime un rapport Z via les commandes AIDL natives Sunmi.
 *
 * Traçabilité refus silencieux (hotfix 2026-06-02) — auparavant retour false
 * sans contexte rendait impossible le diagnostic à distance.
 */
export async function sendViaAidlZReport(closure: ZReportData): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge AIDL Sunmi non typé (objet Android exposé via WebView)
  const w = window as any;
  const b = w.SunmiPrinter || w.SunmiInnerPrinter;
  if (!b) {
    console.warn(
      '[PrintService] sendViaAidlZReport: AIDL bridge absent (SunmiPrinter/SunmiInnerPrinter non exposés)',
    );
    return false;
  }
  const num = (k: keyof ZReportData) => Number(closure[k] ?? 0);
  const str = (k: keyof ZReportData) => String(closure[k] ?? '');
  try {
    if (typeof b.beginTransaction === 'function') b.beginTransaction();
    if (typeof b.setAlignment === 'function') b.setAlignment(1);
    if (typeof b.setBold === 'function') b.setBold(true);
    if (typeof b.setFontSize === 'function') b.setFontSize(28);
    b.printText(str('store_name') || 'VisionPOS');
    if (typeof b.setBold === 'function') b.setBold(false);
    if (typeof b.setFontSize === 'function') b.setFontSize(22);
    if (closure.store_siret) b.printText('SIRET ' + str('store_siret'));
    b.printText('================================');
    if (typeof b.setBold === 'function') b.setBold(true);
    if (typeof b.setFontSize === 'function') b.setFontSize(28);
    const isXReport = String(closure.report_kind || '').includes('x');
    b.printText((isXReport ? 'RAPPORT X' : 'CLOTURE Z') + ' N°' + str('closure_number'));
    if (typeof b.setBold === 'function') b.setBold(false);
    if (typeof b.setFontSize === 'function') b.setFontSize(22);
    if (closure.closure_date)
      b.printText(new Date(String(closure.closure_date)).toLocaleString('fr-FR'));
    b.printText('================================');
    if (typeof b.setAlignment === 'function') b.setAlignment(0);
    if (closure.register_name) b.printText('Caisse: ' + str('register_name'));
    if (closure.user_name) b.printText('Operateur: ' + str('user_name'));
    b.printText('--------------------------------');
    b.printText('Transactions: ' + num('transaction_count'));
    b.printText('CA TTC      : ' + num('total_ttc').toFixed(2) + ' EUR');
    b.printText('CA HT       : ' + num('total_ht').toFixed(2) + ' EUR');
    b.printText('TVA         : ' + num('total_tva').toFixed(2) + ' EUR');
    if (Array.isArray(closure.tva_details) && closure.tva_details.length > 0) {
      b.printText('--------------------------------');
      b.printText('Detail TVA:');
      for (const v of closure.tva_details) {
        const rate = Number(v?.tva_rate ?? 0).toFixed(1);
        const ht = Number(v?.total_ht ?? 0).toFixed(2);
        const tva = Number(v?.total_tva ?? 0).toFixed(2);
        b.printText('  ' + rate + '% (base ' + ht + ') : ' + tva + ' EUR');
      }
    }
    if (closure.payment_breakdown && typeof closure.payment_breakdown === 'object') {
      b.printText('--------------------------------');
      b.printText('Encaissements:');
      for (const [m, amt] of Object.entries(closure.payment_breakdown)) {
        b.printText('  ' + m.padEnd(18) + Number(amt).toFixed(2).padStart(10) + ' E');
      }
    }
    // Remboursements (avoirs) — miroir escpos-formatter (4.A.16)
    if (num('refunds_count') > 0) {
      b.printText('--------------------------------');
      b.printText('Remboursements: ' + num('refunds_count'));
      b.printText('Total avoirs   : -' + num('refunds_total_ttc').toFixed(2) + ' EUR');
      if (
        closure.refunds_payment_breakdown &&
        typeof closure.refunds_payment_breakdown === 'object'
      ) {
        for (const [m, amt] of Object.entries(closure.refunds_payment_breakdown)) {
          b.printText('  ' + m.padEnd(18) + ('-' + Number(amt).toFixed(2)).padStart(10) + ' E');
        }
      }
    }
    // Règlements d'encours, hors chiffre d'affaires — miroir escpos-formatter (4.A.16)
    const encoursLines = zEncoursLines(closure.encours_settlements, 18);
    if (encoursLines.length > 0) b.printText('--------------------------------');
    for (const line of encoursLines) b.printText(line);
    b.printText('================================');
    if (typeof closure.opening_amount === 'number' || typeof closure.closing_amount === 'number') {
      b.printText('Fond ouverture : ' + Number(closure.opening_amount || 0).toFixed(2) + ' EUR');
      b.printText('Especes theo.  : ' + Number(closure.theoretical_cash || 0).toFixed(2) + ' EUR');
      if (typeof closure.closing_amount === 'number') {
        b.printText('Fond cloture   : ' + Number(closure.closing_amount).toFixed(2) + ' EUR');
      }
      if (typeof closure.cash_diff === 'number') {
        const diff = Number(closure.cash_diff);
        b.printText('Ecart caisse   : ' + (diff >= 0 ? '+' : '') + diff.toFixed(2) + ' EUR');
      }
      if (typeof closure.avg_ticket === 'number') {
        b.printText('Panier moyen   : ' + Number(closure.avg_ticket).toFixed(2) + ' EUR');
      }
      b.printText('================================');
    }
    if (Array.isArray(closure.top_products) && closure.top_products.length > 0) {
      b.printText('TOP VENTES');
      for (const p of closure.top_products) {
        const name = String(p?.name || '')
          .slice(0, 22)
          .padEnd(22);
        const qty = String(p?.qty || 0).padStart(3);
        const total = Number(p?.total || 0)
          .toFixed(2)
          .padStart(7);
        b.printText(name + ' x' + qty + ' ' + total + 'E');
      }
      b.printText('================================');
    }
    if (closure.hash) {
      if (typeof b.setAlignment === 'function') b.setAlignment(1);
      if (typeof b.setFontSize === 'function') b.setFontSize(16);
      b.printText(String(closure.hash).slice(0, 32));
    }
    if (typeof b.setAlignment === 'function') b.setAlignment(1);
    if (typeof b.setFontSize === 'function') b.setFontSize(18);
    if (closure.generated_at) {
      b.printText('Genere le ' + new Date(String(closure.generated_at)).toLocaleString('fr-FR'));
    }
    b.printText(String(closure.software_label || EDITOR.nf525Mention));
    b.printText('Editeur : ' + String(closure.editor || EDITOR.legalName));
    if (typeof b.feedLines === 'function') b.feedLines(4);
    else if (typeof b.lineWrap === 'function') b.lineWrap(4);
    if (typeof b.commitTransaction === 'function') b.commitTransaction();
    if (typeof b.cutPaper === 'function') {
      try {
        b.cutPaper();
      } catch {
        /* ignore — partial cut peut throw selon modèle */
      }
    }
    return true;
  } catch (e) {
    console.warn('[PrintService] AIDL Z report error', e);
    return false;
  }
}

// ── sendViaCloudPrinterZReport ───────────────────────────────────────────────

/**
 * Imprime un rapport Z via le Sunmi Cloud Printer (PrinterX).
 *
 * Traçabilité refus silencieux (hotfix 2026-06-02) — sur D3 Pro le Cloud
 * Printer peut être absent (firmware/bridge non chargé) ; sans log on ne
 * pouvait pas distinguer "pas de matériel" de "matériel KO".
 */
export async function sendViaCloudPrinterZReport(closure: ZReportData): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge PrinterX Sunmi non typé (objet Android exposé via WebView)
  const w = window as any;
  const px = w.SunmiPrinterX;
  if (!px?.lineApiPrintText) {
    console.warn(
      '[PrintService] sendViaCloudPrinterZReport: PrinterX.lineApiPrintText absent (Cloud Printer non disponible)',
    );
    return false;
  }
  try {
    const num = (k: keyof ZReportData) => Number(closure[k] ?? 0);
    const str = (k: keyof ZReportData) => String(closure[k] ?? '');
    const isXReport = String(closure.report_kind || '').includes('x');
    const lines: string[] = [];
    lines.push(str('store_name') || 'VisionPOS');
    if (closure.store_siret) lines.push('SIRET ' + str('store_siret'));
    lines.push('================================');
    lines.push((isXReport ? 'RAPPORT X' : 'CLOTURE Z') + ' N°' + str('closure_number'));
    if (closure.closure_date)
      lines.push(new Date(String(closure.closure_date)).toLocaleString('fr-FR'));
    lines.push('================================');
    if (closure.register_name) lines.push('Caisse: ' + str('register_name'));
    if (closure.user_name) lines.push('Operateur: ' + str('user_name'));
    lines.push('--------------------------------');
    lines.push('Transactions: ' + num('transaction_count'));
    lines.push('CA TTC      : ' + num('total_ttc').toFixed(2) + ' EUR');
    lines.push('CA HT       : ' + num('total_ht').toFixed(2) + ' EUR');
    lines.push('TVA         : ' + num('total_tva').toFixed(2) + ' EUR');
    if (Array.isArray(closure.tva_details) && closure.tva_details.length > 0) {
      lines.push('Detail TVA:');
      for (const v of closure.tva_details) {
        const rate = Number(v?.tva_rate ?? 0).toFixed(1);
        const ht = Number(v?.total_ht ?? 0).toFixed(2);
        const tva = Number(v?.total_tva ?? 0).toFixed(2);
        lines.push('  ' + rate + '% (base ' + ht + ') : ' + tva + ' EUR');
      }
    }
    if (closure.payment_breakdown && typeof closure.payment_breakdown === 'object') {
      lines.push('Encaissements:');
      for (const [m, amt] of Object.entries(closure.payment_breakdown)) {
        lines.push('  ' + m.padEnd(18) + Number(amt).toFixed(2).padStart(10) + ' E');
      }
    }
    // Remboursements (avoirs) — miroir escpos-formatter (4.A.16)
    if (num('refunds_count') > 0) {
      lines.push('Remboursements: ' + num('refunds_count'));
      lines.push('Total avoirs   : -' + num('refunds_total_ttc').toFixed(2) + ' EUR');
      if (
        closure.refunds_payment_breakdown &&
        typeof closure.refunds_payment_breakdown === 'object'
      ) {
        for (const [m, amt] of Object.entries(closure.refunds_payment_breakdown)) {
          lines.push('  ' + m.padEnd(18) + ('-' + Number(amt).toFixed(2)).padStart(10) + ' E');
        }
      }
    }
    // Règlements d'encours, hors chiffre d'affaires — miroir escpos-formatter (4.A.16)
    lines.push(...zEncoursLines(closure.encours_settlements, 18));
    lines.push('================================');
    if (typeof closure.opening_amount === 'number' || typeof closure.closing_amount === 'number') {
      lines.push('Fond ouverture : ' + Number(closure.opening_amount || 0).toFixed(2) + ' EUR');
      lines.push('Especes theo.  : ' + Number(closure.theoretical_cash || 0).toFixed(2) + ' EUR');
      if (typeof closure.closing_amount === 'number') {
        lines.push('Fond cloture   : ' + Number(closure.closing_amount).toFixed(2) + ' EUR');
      }
      if (typeof closure.cash_diff === 'number') {
        const diff = Number(closure.cash_diff);
        lines.push('Ecart caisse   : ' + (diff >= 0 ? '+' : '') + diff.toFixed(2) + ' EUR');
      }
      if (typeof closure.avg_ticket === 'number') {
        lines.push('Panier moyen   : ' + Number(closure.avg_ticket).toFixed(2) + ' EUR');
      }
      lines.push('================================');
    }
    if (Array.isArray(closure.top_products) && closure.top_products.length > 0) {
      lines.push('TOP VENTES');
      for (const p of closure.top_products) {
        const name = String(p?.name || '')
          .slice(0, 22)
          .padEnd(22);
        const qty = String(p?.qty || 0).padStart(3);
        const total = Number(p?.total || 0)
          .toFixed(2)
          .padStart(7);
        lines.push(name + ' x' + qty + ' ' + total + 'E');
      }
      lines.push('================================');
    }
    if (closure.hash) lines.push(String(closure.hash).slice(0, 32));
    if (closure.generated_at) {
      lines.push('Genere le ' + new Date(String(closure.generated_at)).toLocaleString('fr-FR'));
    }
    lines.push(String(closure.software_label || EDITOR.nf525Mention));
    lines.push('Editeur : ' + String(closure.editor || EDITOR.legalName));
    lines.push('\n\n\n');
    const text = lines.join('\n') + '\n';
    px.lineApiPrintText(text);
    if (typeof px.lineApiCutPaper === 'function') px.lineApiCutPaper();
    return true;
  } catch (e) {
    console.warn('[PrintService] Cloud Printer Z report error', e);
    return false;
  }
}
